import { FC, useMemo, useState } from 'react';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import { createColumnHelper } from '@tanstack/react-table';
import { DebouncedInput } from '@/components/functional/DebouncedInput/DebouncedInput.tsx';
import SelectInput from '@/components/functional/SelectInput/SelectInput.tsx';

type Person = {
  firstName: string;
  lastName: string;
  age: number;
  visits: number;
  status: string;
  progress: number;
};

const defaultData: Person[] = [
  {
    firstName: 'tanner',
    lastName: 'linsley',
    age: 24,
    visits: 100,
    status: 'In Relationship',
    progress: 50,
  },
  {
    firstName: 'tandy',
    lastName: 'miller',
    age: 40,
    visits: 40,
    status: 'Single',
    progress: 80,
  },
  {
    firstName: 'joe',
    lastName: 'dirte',
    age: 45,
    visits: 20,
    status: 'Complicated',
    progress: 10,
  },
];

const columnHelper = createColumnHelper<Person>();

const columns = [
  columnHelper.accessor('firstName', {
    cell: (info) => info.getValue(),
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor((row) => row.lastName, {
    id: 'lastName',
    cell: (info) => <i>{info.getValue()}</i>,
    header: () => <span>Last Name</span>,
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('age', {
    header: () => 'Age',
    cell: (info) => info.renderValue(),
    footer: (info) => info.column.id,
    enableSorting: false,
  }),
  columnHelper.accessor('visits', {
    header: () => <span>Visits</span>,
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('progress', {
    header: 'Profile Progress',
    footer: (info) => info.column.id,
  }),
];

const DataTablePage: FC = () => {
  const [filter, setFilter] = useState('');
  const [status, setStatus] = useState('');

  const filters = useMemo(
    () => [
      {
        id: 'firstName',
        value: filter,
      },
      {
        id: 'status',
        value: status,
      },
    ],
    [filter, status]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4">
        <DebouncedInput
          wrapperClassName="col-span-2 lg:col-span-1"
          placeholder="Search first name"
          value={filter}
          onChange={(val) => setFilter(`${val}`)}
        />
        <SelectInput
          wrapperClassName="col-span-2 lg:col-span-1"
          placeholder="Select status"
          value={status}
          onChange={(val) => setStatus(val)}
          options={[
            {
              value: 'Single',
              label: 'Single',
            },
            {
              value: 'Complicated',
              label: 'Complicated',
            },
            {
              value: 'In Relationship',
              label: 'In Relationship',
            },
          ]}
        />
      </div>
      <DataTable
        filters={filters}
        data={defaultData}
        columns={columns}
        wrapperClassName="max-h-[400px] rounded-2xl border border-base-300"
      />
    </div>
  );
};

export default DataTablePage;
